// /admin/v2/chapters — Notes (by chapter): Biology chapters, notes status, readiness numbers
// and the Release switch (BRIEFS C4.4, SPEC §7); each row opens the chapter's sections page
// (C11.7). Released chapters are the only ones Harsh sees in /app.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { v2AdminChapters, v2SetRelease, v2SetStatus, errText } from '../../../api/v2admin';
import V2Tabs, { Msg } from './V2Tabs';

const num = (n) => Number(n || 0).toLocaleString('en-IN');

function Switch({ on, disabled, onClick, label }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled}
      onClick={onClick} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, border: 0, background: 'transparent',
        cursor: disabled ? 'default' : 'pointer', padding: 4, opacity: disabled ? 0.5 : 1,
      }}>
      <span style={{
        width: 42, height: 24, borderRadius: 12, position: 'relative', transition: 'background .15s',
        background: on ? '#047857' : '#CBD5E1', flexShrink: 0,
      }}>
        <span style={{
          position: 'absolute', top: 3, left: on ? 21 : 3, width: 18, height: 18, borderRadius: 9,
          background: 'white', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.2)',
        }} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color: on ? '#047857' : 'var(--text-muted)' }}>
        {on ? 'Released' : 'Not released'}
      </span>
    </button>
  );
}

export default function AdminV2Chapters() {
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['v2-admin-chapters'],
    queryFn: v2AdminChapters,
  });

  const run = async (c, fn, okText) => {
    setBusy(c.chapter_id); setMsg(null);
    try {
      await fn();
      setMsg({ kind: 'ok', text: okText });
      await refetch();
    } catch (e) {
      setMsg({ kind: 'err', text: errText(e, 'Could not change that.') });
    } finally { setBusy(null); }
  };

  const release = (c) => {
    const on = !c.is_released;
    if (!on && !window.confirm(`Hide "${c.title}" from Harsh? His highlights and answers stay saved.`)) return;
    run(c, () => v2SetRelease(c.chapter_id, on),
      on ? `"${c.title}" is now open for the students.` : `"${c.title}" is hidden from the students again.`);
  };
  const status = (c) => {
    const next = c.notes_status === 'approved' ? 'draft' : 'approved';
    run(c, () => v2SetStatus(c.chapter_id, next),
      next === 'approved' ? `Notes of "${c.title}" marked approved.` : `Notes of "${c.title}" set back to draft.`);
  };

  const rows = data || [];
  const withNotes = rows.filter((c) => c.points > 0).length;
  const released = rows.filter((c) => c.is_released).length;

  return (
    <div>
      <V2Tabs title="Notes (by chapter)"
        intro="Every Biology chapter of the new notes. Press 'Open sections & notes' to see a chapter section by section, exactly as NCERT prints it, and to put notes into its sections. A chapter shows in Harsh's app only when it is released. 'Not covered' counts the points that no confirmed question and no one-liner asks about yet." />
      <Msg msg={msg} />
      <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', padding: '14px 18px' }}>
        <span className="badge badge-green">{released} released</span>
        <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>{withNotes} of {rows.length} chapters have notes</span>
        <span style={{ flex: 1 }} />
      </div>
      {isLoading ? <div className="spinner" /> : isError ? (
        <div className="card" style={{ color: 'var(--danger)' }}>Could not load the chapters.</div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table" data-testid="v2-admin-chapters">
            <thead>
              <tr>
                <th>Chapter</th>
                <th>Notes</th>
                <th style={{ textAlign: 'right' }}>Units</th>
                <th style={{ textAlign: 'right' }}>Points</th>
                <th style={{ textAlign: 'right' }}>Questions linked</th>
                <th style={{ textAlign: 'right' }}>One-liners</th>
                <th style={{ textAlign: 'right' }}>Clips</th>
                <th style={{ textAlign: 'right' }}>Not covered</th>
                <th>Release</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const has = c.points > 0;
                return (
                  <tr key={c.chapter_id} data-testid={`v2-admin-chapter-${c.chapter_id}`}>
                    <td>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Class {c.class_level} · Chapter {c.ncert_no}</div>
                      <div style={{ fontWeight: 600 }}>{c.title}</div>
                      <Link to={`/admin/v2/chapters/${c.chapter_id}/structure`} className="btn btn-primary btn-sm"
                        data-testid={`v2-open-structure-${c.chapter_id}`}
                        style={{ textDecoration: 'none', marginTop: 6, display: 'inline-flex' }}>
                        Open sections &amp; notes
                      </Link>
                      {has && (
                        <div style={{ display: 'flex', gap: 10, marginTop: 4, fontSize: 12, color: 'var(--text-muted)' }}>
                          Advanced:
                          <Link to={`/admin/v2/points?chapter=${c.chapter_id}`}>Edit points</Link>
                          <Link to={`/admin/v2/module-map?chapter=${c.chapter_id}`}>Module map</Link>
                        </div>
                      )}
                    </td>
                    <td>
                      {!has ? <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>No notes yet</span> : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                          <span className={`badge ${c.notes_status === 'approved' ? 'badge-green' : 'badge-yellow'}`}>
                            {c.notes_status === 'approved' ? 'Approved' : 'Draft'}
                          </span>
                          <button className="btn btn-ghost btn-sm" disabled={busy === c.chapter_id} onClick={() => status(c)}>
                            {c.notes_status === 'approved' ? 'Back to draft' : 'Approve'}
                          </button>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>version {c.notes_version}</span>
                        </div>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{num(c.units)}</td>
                    <td style={{ textAlign: 'right' }}>{num(c.points)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {c.linked_pct}%
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{num(c.questions_linked)} of {num(c.questions)}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>{num(c.one_liners)}</td>
                    <td style={{ textAlign: 'right' }}>{num(c.clips)}</td>
                    <td style={{ textAlign: 'right' }}>{has ? num(c.uncovered_points) : '—'}</td>
                    <td>
                      <Switch on={c.is_released} disabled={busy === c.chapter_id || (!has && !c.is_released)}
                        onClick={() => release(c)} label={`Release ${c.title}`} />
                      {c.is_released && c.released_at && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>since {new Date(c.released_at).toLocaleDateString('en-IN')}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
